import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { HiOutlineShieldCheck, HiOutlineUserCircle } from "react-icons/hi2";
import fixNumbers, { contextApi } from "../context/contextApi";
import { login, sendOtp } from "../services/Login";
import { Loading } from "../components/common/Loading";
import { handleToast } from "../services/config";

const OTP_LENGTH = 5;

const maskMobile = (mobile) => {
  if (!mobile || mobile.length < 4) return mobile;
  return `${mobile.slice(0, 4)}•••${mobile.slice(-2)}`;
};

const formatTimer = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const Login = () => {
  const [timer, setTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false); // برای نمایش پسورد
  const { register, handleSubmit, formState: { errors } } = useForm();
  const {
    register: register2,
    handleSubmit: handleSubmit2,
    setValue: setValue2,
    formState: { errors: errors2 },
  } = useForm();
  const context = useContext(contextApi);
  const [code, setCode] = useState(false);
  const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(""));
  const otpRefs = useRef([]);
  const [token, setToken] = useState('');
  const [user, setUser] = useState({ mobile: '', password: '' });
  const route = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('login') !== undefined) route('/');
    context.setLoad(false);
  }, []);

  useEffect(() => {
    const storedStartTime = localStorage.getItem("startTime");
    const currentTime = Math.floor(Date.now() / 1000);
    const elapsedTime = currentTime - parseInt(storedStartTime, 10);
    if (elapsedTime < 120) setTimer(120 - elapsedTime);
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer(prev => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  useEffect(() => {
    if (!code) return;
    setOtpValues(Array(OTP_LENGTH).fill(""));
    setValue2("code", "");
    const focusTimer = setTimeout(() => otpRefs.current[0]?.focus(), 150);
    return () => clearTimeout(focusTimer);
  }, [code, setValue2]);

  const syncOtpToForm = (values) => {
    setValue2("code", values.join(""), { shouldValidate: true });
  };

  const handleOtpChange = (index, value) => {
    const digit = fixNumbers(value).replace(/\D/g, "").slice(-1);
    const next = [...otpValues];
    next[index] = digit;
    setOtpValues(next);
    syncOtpToForm(next);
    if (digit && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = fixNumbers(e.clipboardData.getData("text"))
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((d, i) => {
      next[i] = d;
    });
    setOtpValues(next);
    syncOtpToForm(next);
    otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleResendOtp = async () => {
    if (timer > 0 || !user.mobile) return;
    const startTime = Math.floor(Date.now() / 1000);
    localStorage.setItem("startTime", startTime.toString());
    setTimer(120);
    context.setLoad(true);
    try {
      await sendOtp({ mobile: user.mobile });
      handleToast("success", "کد مجدداً ارسال شد");
      setOtpValues(Array(OTP_LENGTH).fill(""));
      setValue2("code", "");
      otpRefs.current[0]?.focus();
    } catch (error) {}
    context.setLoad(false);
  };

  const handleBackToLogin = () => {
    setCode(false);
    setOtpValues(Array(OTP_LENGTH).fill(""));
    setValue2("code", "");
  };

  const onSubmit = async (data) => {
    const startTime = Math.floor(Date.now() / 1000);
    localStorage.setItem("startTime", startTime.toString());
    setTimer(120);
    context.setLoad(true);

    const data2 = { mobile: data.userName, password: fixNumbers(data.password) };
    setUser({ ...user, mobile: data.userName, password: data.password });

    try {
      let res = await login(data2);
      setToken(res.data.data.token);
      handleToast('success', 'کد ارسال شده را وارد کنید');
      setCode(true);
      await sendOtp({ mobile: data.userName });
    } catch (error) {
      setTimer(0);
      localStorage.removeItem("startTime");
    }
    context.setLoad(false);
  };

  const onSubmit2 = async (data) => {
    context.setLoad(true);
    const data2 = { mobile: user.mobile, password: fixNumbers(user.password), otp: parseInt(data.code) };
    try {
      let res = await login(data2);
      setToken(res.data.data.token);
      handleToast('success', 'ورود موفق');
      route('/');
      context.setLogin(true);
      localStorage.setItem('login', token);
      localStorage.setItem('mobile', user.mobile);
    } catch (error) {}
    context.setLoad(false);
  };

  return (
    <div className="login-page h-screen">
      <Loading show={context.load} />
      <ToastContainer />
      <div className="w-full h-screen flex items-center justify-center flex-col backdrop-blur-sm bg-black/40">
        <h1 className="text-3xl text-white font-bold mb-6 drop-shadow-lg">خوش آمدید</h1>

        {/* فرم ورود */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`${
            code ? "hidden" : ""
          } w-11/12 max-w-md p-8 bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/25 transition-all`}
        >
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-blue-500/20 border border-blue-400/40 flex items-center justify-center mb-4 shadow-lg">
              <HiOutlineUserCircle className="text-3xl text-blue-300" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">ورود به حساب</h2>
            <p className="text-sm text-white/70">
              نام کاربری و رمز عبور خود را وارد کنید
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm text-white/90 mb-2 font-medium">
              نام کاربری
            </label>
            <input
              {...register("userName", { required: true })}
              type="text"
              className={`w-full px-4 py-3 rounded-xl text-sm bg-white/95 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors["userName"] ? "ring-2 ring-red-400 bg-red-50" : ""
              }`}
              placeholder="نام کاربری خود را وارد کنید"
            />
            {errors["userName"] && (
              <p className="text-red-300 text-xs mt-2">لطفا نام کاربری خود را وارد کنید</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm text-white/90 mb-2 font-medium">
              رمز عبور
            </label>
            <div className="relative">
              <input
                {...register("password", { required: true })}
                type={showPassword ? "text" : "password"}
                dir="ltr"
                className={`w-full px-4 py-3 pl-11 rounded-xl text-sm bg-white/95 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  errors["password"] ? "ring-2 ring-red-400 bg-red-50" : ""
                }`}
                placeholder="رمز عبور خود را وارد کنید"
              />
              <button
                type="button"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "مخفی کردن رمز عبور" : "نمایش رمز عبور"}
              >
                {showPassword ? (
                  <AiOutlineEyeInvisible size={20} />
                ) : (
                  <AiOutlineEye size={20} />
                )}
              </button>
            </div>
            {errors["password"] && (
              <p className="text-red-300 text-xs mt-2">لطفا رمز عبور خود را وارد کنید</p>
            )}
          </div>

          <button
            type="submit"
            disabled={timer > 0}
            className={`w-full py-3 rounded-xl text-white font-semibold shadow-lg transition duration-300 ${
              timer > 0
                ? "bg-gray-500/70 cursor-not-allowed text-sm"
                : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            }`}
          >
            {timer > 0 ? `ارسال مجدد تا ${timer} ثانیه` : "ورود"}
          </button>
        </form>

        {/* فرم تایید کد */}
        <form
          onSubmit={handleSubmit2(onSubmit2)}
          className={`${
            !code ? "hidden" : ""
          } w-11/12 max-w-md p-8 bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/25 transition-all`}
        >
          <input
            type="hidden"
            {...register2("code", {
              required: true,
              minLength: OTP_LENGTH,
              maxLength: OTP_LENGTH,
            })}
          />

          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-blue-500/20 border border-blue-400/40 flex items-center justify-center mb-4 shadow-lg">
              <HiOutlineShieldCheck className="text-3xl text-blue-300" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">تایید هویت</h2>
            <p className="text-sm text-white/70 leading-relaxed">
              کد {OTP_LENGTH} رقمی ارسال‌شده به شماره
              <span className="block mt-1 font-semibold text-white tracking-wide" dir="ltr">
                {maskMobile(user.mobile)}
              </span>
              را وارد کنید
            </p>
          </div>

          <div className="flex justify-center gap-2 sm:gap-3 mb-2" dir="ltr">
            {otpValues.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  otpRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={index === 0 ? handleOtpPaste : undefined}
                className={`w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold rounded-xl bg-white/95 text-gray-800 shadow-inner transition-all focus:outline-none focus:ring-2 focus:ring-blue-400 focus:scale-105 ${
                  errors2.code ? "ring-2 ring-red-400" : ""
                }`}
              />
            ))}
          </div>

          {errors2.code && (
            <p className="text-red-400 text-xs text-center mt-2 mb-4">
              لطفاً کد {OTP_LENGTH} رقمی را کامل وارد کنید
            </p>
          )}

          <button
            type="submit"
            className="w-full py-3 mt-4 rounded-xl text-white font-semibold shadow-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition duration-300"
          >
            تایید و ورود
          </button>

          <div className="mt-5 pt-4 border-t border-white/15 flex flex-col items-center gap-3 text-sm">
            {timer > 0 ? (
              <p className="text-white/60">
                ارسال مجدد کد تا{" "}
                <span className="font-mono text-white/90">{formatTimer(timer)}</span>
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendOtp}
                className="text-blue-300 hover:text-blue-200 font-medium transition-colors"
              >
                ارسال مجدد کد
              </button>
            )}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-white/50 hover:text-white/80 transition-colors"
            >
              بازگشت به ورود
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
